// src/components/FrameworkSelector.js
import React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  Paper,
  Stack,
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Button,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import { getCatalog } from "../api";
import { getPairedId, groupIntoPairs } from "../utils/mluPairingUtils";

// ---------------------------------------------------------------------
// Fallback MLU lists
// ---------------------------------------------------------------------
const EDU_MLUS = [
  { code: "EDU-101", title: "Imagining Innovation Agency" },
  { code: "EDU-102", title: "Defining Systems Innovation" },
  { code: "EDU-103", title: "Public Sector Innovation" },
  { code: "EDU-104", title: "Organisational Development" },
  { code: "EDU-105", title: "Ecosystem Awareness" },
  { code: "EDU-106", title: "Triple Loop Learning" },
  { code: "EDU-201", title: "Focusing on Public Value" },
  { code: "EDU-202", title: "Processes of Public Value Creation" },
  { code: "EDU-203", title: "Expanding the Concept of Value" },
  { code: "EDU-204", title: "Exploring the Value of Resilience" },
  { code: "EDU-205", title: "Your Organization’s Value Proposition" },
  { code: "EDU-206", title: "Foresight: Envision and Planning for the Future" },
  { code: "EDU-301", title: "Defining Decision Making Processes" },
  { code: "EDU-302", title: "Mapping Decision Making Processes" },
  {
    code: "EDU-303",
    title: "Imagining Decision-Making Support as Urban Data Ecosystem",
  },
  { code: "EDU-304", title: "Designing Stakeholder Engagement" },
  { code: "EDU-305", title: "Local Decision-Making Use Case" },
  { code: "EDU-306", title: "Design to Engage" },
];

const GOV_MLUS = [
  { code: "GOV-101", title: "What Is Business Model" },
  { code: "GOV-102", title: "Social/Non-Profit Business Models" },
  { code: "GOV-103", title: "Business Model Canvas" },
  {
    code: "GOV-104",
    title: "Non Profit Business Model Canvas / City Model Canvas",
  },
  {
    code: "GOV-105",
    title: "Application of Nonprofit Business Model Canvas in LDT Context",
  },
  { code: "GOV-106", title: "Evaluation of Local Digital Twin Business Models" },
  { code: "GOV-201", title: "Data Sharing and Interoperability Basics" },
  {
    code: "GOV-202",
    title: "Role of Local Governance on Data Sharing and Interoperability",
  },
  {
    code: "GOV-203",
    title: "Basic Interoperability Principles for Local Digital Twins",
  },
  {
    code: "GOV-204",
    title: "Existing Interoperability Efforts and Tools for Local Digital Twins",
  },
  {
    code: "GOV-205",
    title: "Application of Data Sharing & Interoperability Standards",
  },
  {
    code: "GOV-206",
    title: "Development & Improvement of Interoperability Standards",
  },
  {
    code: "GOV-301",
    title: "Foundational Concepts of Citizen Participation and Public Engagement",
  },
  {
    code: "GOV-302",
    title: "The Role of Digital Technology in Citizen Participation",
  },
  { code: "GOV-303", title: "E-Governance at the Local Level" },
  { code: "GOV-304", title: "Local Digital Twins and the Citizen's Role" },
  {
    code: "GOV-305",
    title: "Participatory Planning Strategies through LDT Interfaces",
  },
  {
    code: "GOV-306",
    title: "Evaluation of Citizen Participation & Engagement Levels",
  },
];

const TEC_MLUS = [
  { code: "TEC-101", title: "Foundations of Digital Twin Cities" },
  { code: "TEC-102", title: "The Five-Layer Architecture Framework" },
  { code: "TEC-103", title: "Software Design Principles for Digital Twins" },
  { code: "TEC-104", title: "Integration of Physical and Digital Systems" },
  {
    code: "TEC-105",
    title: "Interoperability and Standards in Digital Twin Design",
  },
  { code: "TEC-106", title: "Case Studies of Local Digital Twin Architecture" },
  { code: "TEC-201", title: "Introduction to Data Flows in Digital Twins" },
  { code: "TEC-202", title: "Data Sharing Processes and Governance" },
  { code: "TEC-203", title: "Data Sharing Infrastructure" },
  { code: "TEC-204", title: "Real-Time Data Flows in Digital Twins" },
  { code: "TEC-205", title: "Security and Privacy in Data Flows" },
  { code: "TEC-206", title: "Applications of Efficient Data Flows" },
  { code: "TEC-301", title: "AI Foundations in Digital Twins" },
  {
    code: "TEC-302",
    title: "Data Processing and Machine Learning in Digital Twins",
  },
  { code: "TEC-303", title: "Predictive Analytics for Smart Digital Twins" },
  {
    code: "TEC-304",
    title: "Deep Learning Model Applications in Digital Twins",
  },
  { code: "TEC-305", title: "Ethical AI in Digital Twins" },
  { code: "TEC-306", title: "Cutting-Edge AI Applications in Digital Twins" },
];

const EID_MLUS = [
  { code: "EID-101", title: "Fear the Unknown – Trust/Confidence" },
  { code: "EID-102", title: "Citymelion – Strategic Adaptation / Resilience" },
  { code: "EID-103", title: "Trust in Data and Data Handling – Explainability" },
  { code: "EID-104", title: "Biases: Gender Data Gap + Lack of Data" },
  { code: "EID-105", title: "Balance Transparency, Communication and Workflow" },
  { code: "EID-106", title: "AI, Twinning and Agora" },
  { code: "EID-201", title: "The Purpose of the LDT for Doing Good" },
  { code: "EID-202", title: "Accessibility in Two Layers" },
  { code: "EID-203", title: "Mission Green Web – Mission Green Twin?" },
  { code: "EID-204", title: "Visuals and Narratives for LDTs" },
  { code: "EID-205", title: "Human in the Loop / On the Loop / In Command" },
  {
    code: "EID-206",
    title: "Human-Centered Design – Gender-Equitable Urban Planning",
  },
  {
    code: "EID-301",
    title: "Collaboration: Citizen Engagement for Citizen Participation",
  },
  {
    code: "EID-302",
    title: "Communication: User-Oriented / Universal Design",
  },
  { code: "EID-303", title: "Easy Access and Fun – Playful Design" },
  { code: "EID-304", title: "One-Click Participation" },
  { code: "EID-305", title: "Idea-a-thon: Use Case Coming from Citizens" },
  { code: "EID-306", title: "Immersive Experiences" },
];

// ---------------------------------------------------------------------
// Catalog fetch cache
// ---------------------------------------------------------------------
let catalogCache = null;
let catalogPromise = null;

async function fetchCatalogOnce() {
  if (catalogCache) return catalogCache;

  if (!catalogPromise) {
    catalogPromise = getCatalog()
      .then((data) => {
        const items = data?.items || [];
        catalogCache = items;
        return catalogCache;
      })
      .catch((err) => {
        console.error("[FrameworkSelector] Failed to load catalog:", err);
        catalogCache = null;
        throw err;
      });
  }
  return catalogPromise;
}

// ---------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------
const BG_COLORS = {
  EDU: "#ffebee",
  GOV: "#e8f5e9",
  TEC: "#e3f2fd",
  EID: "#fff8e1",
};

const MAIN_COLORS = {
  EDU: "#c62828",
  GOV: "#2e7d32",
  TEC: "#1565c0",
  EID: "#f9a825",
};

// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
function FrameworkSelector({ label, codePrefix, mlus, selected, certificationNeeded, onToggle, aiIds = [], aiWhy = {} }) {
  const [open, setOpen] = React.useState(false);
  const [mlusFromBackend, setMlusFromBackend] = React.useState(null);
  const [backendError, setBackendError] = React.useState(null);

  const upperPrefix = (codePrefix || "").toUpperCase();
  const bgColor = BG_COLORS[upperPrefix] || "background.paper";
  const badgeColor = MAIN_COLORS[upperPrefix] || "#424242";
  const imageSrc = `${process.env.PUBLIC_URL}/${upperPrefix}.jpg`;

  React.useEffect(() => {
    let cancelled = false;
    fetchCatalogOnce()
      .then((items) => {
        if (cancelled || !items) return;
        const filtered = items
          .filter((item) =>
            (item.id || "").toUpperCase().startsWith(`${codePrefix}-`)
          )
          .map((item) => ({
            id: item.id,
            title: item.title || "",
            module: item.module || "",
            eqf: item.eqf || "",
            outcome: item.outcome || "",
          }));
        setMlusFromBackend(filtered);
        setBackendError(null);
      })
      .catch((err) => {
        if (!cancelled) {
          setBackendError(err.message || "Failed to load catalog");
          setMlusFromBackend(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [codePrefix]);

  // Fallback MLUs – keep shape compatible with catalog entries
  const fallbackMlus = (mlus || []).map((m) => ({
    id: m.id || m.code || "",
    title: m.title || "",
    module: m.module || "",
    eqf: m.eqf || "",
    outcome: m.outcome || "",
  }));

  const effectiveMlus = mlusFromBackend || fallbackMlus;
  const totalSelected = selected.length;

  // Handle toggling with optional pairing
  const handleChipClick = (mluId) => {
    if (certificationNeeded) {
      const pairedId = getPairedId(mluId);
      const hasPair = pairedId && effectiveMlus.some((m) => m.id === pairedId);
      if (hasPair) {
        // Both should be toggled together
        const isActive = selected.includes(mluId);
        if (isActive) {
          // Deselect both
          onToggle(mluId);
          if (selected.includes(pairedId)) onToggle(pairedId);
        } else {
          // Select both
          onToggle(mluId);
          if (!selected.includes(pairedId)) onToggle(pairedId);
        }
        return;
      }
    }
    // Default: single toggle
    onToggle(mluId);
  };

  // Build chip rendering for a single MLU
  const renderChip = (mlu) => {
    const active = selected.includes(mlu.id);
    // Marked by the tool from the assistant's prior-knowledge summary (step 4)
    const byAi = active && aiIds.includes(mlu.id);
    const tooltipContent = (
      <Box sx={{ maxWidth: 320 }}>
        {byAi && (
          <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
            <strong>🤖 Marked by the tool</strong>{aiWhy[mlu.id] ? `: ${aiWhy[mlu.id]}` : ""}
          </Typography>
        )}
        {mlu.title && (
          <Typography variant="caption" display="block">
            <strong>Title:</strong> {mlu.title}
          </Typography>
        )}
        {mlu.eqf && (
          <Typography variant="caption" display="block">
            <strong>EQF:</strong> {mlu.eqf}
          </Typography>
        )}
        {mlu.outcome && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            <strong>Learning outcome:</strong> {mlu.outcome}
          </Typography>
        )}
      </Box>
    );

    return (
      <Tooltip key={mlu.id} title={tooltipContent} arrow>
        <Chip
          clickable
          label={`${byAi ? "🤖 " : ""}${mlu.id} · ${mlu.title}`}
          onClick={() => handleChipClick(mlu.id)}
          sx={{
            m: 0.5,
            height: "auto",
            minHeight: 32,
            "& .MuiChip-label": {
              whiteSpace: "normal",
              display: "block",
              py: 0.75,
              px: 1.5,
            },
            transition: "all 0.15s ease-in-out",
            ...(active
              ? {
                fontWeight: 700,
                fontSize: "0.85rem",
                color: upperPrefix === "EID" ? "rgba(0,0,0,0.87)" : "#ffffff",
                backgroundColor: badgeColor,
                border: "2px solid",
                borderColor: badgeColor,
                boxShadow: `0px 4px 0px ${badgeColor}99, 0px 4px 8px rgba(0,0,0,0.2)`,
                transform: "translateY(-4px)",
                "&:hover": {
                  backgroundColor: badgeColor,
                  transform: "translateY(-3px)",
                  boxShadow: `0px 3px 0px ${badgeColor}99, 0px 3px 6px rgba(0,0,0,0.2)`,
                },
              }
              : {
                fontWeight: 500,
                fontSize: "0.8125rem",
                color: "text.secondary",
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "none",
                transform: "none",
                "&:hover": {
                  backgroundColor: "action.hover",
                  borderColor: "text.primary",
                },
              }),
          }}
        />
      </Tooltip>
    );
  };

  // Render chips: either grouped in pairs or individually
  const renderChips = () => {
    if (certificationNeeded) {
      const pairs = groupIntoPairs(effectiveMlus);
      return pairs.map((group) => {
        if (group.length === 2) {
          // Render as a stacked pair with a visual link
          return (
            <Box
              key={`pair-${group[0].id}`}
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "stretch",
                border: "2px dashed",
                borderColor: "primary.light",
                borderRadius: 2,
                p: 0.5,
                m: 0.5,
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                position: "relative",
              }}
            >
              <Box sx={{
                position: "absolute",
                top: -10,
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "primary.main",
                color: "white",
                borderRadius: "50%",
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
              }}>
                <LinkIcon sx={{ fontSize: 14 }} />
              </Box>
              {renderChip(group[0])}
              {renderChip(group[1])}
            </Box>
          );
        }
        // Unpaired MLU
        return renderChip(group[0]);
      });
    }
    // Normal mode: flat list
    return effectiveMlus.map((mlu) => renderChip(mlu));
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        mb: 2,
        backgroundColor: bgColor,
      }}
    >
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          {label && (
            <Typography variant="subtitle1" sx={{ mb: 0.3 }}>
              {label}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: "break-word" }}>
            Prefix: {codePrefix} · {effectiveMlus.length} MLUs
            {certificationNeeded && " · 🔗 Certification mode"}
          </Typography>

          {totalSelected > 0 && !open && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: "break-word" }}>
              Selected: {selected.join(", ")}
            </Typography>
          )}
          {backendError && (
            <Typography variant="caption" color="error" display="block">
              Using fallback list (catalog error)
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {/* Counter circle (always shown) */}
          <Box
            sx={{
              minWidth: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: badgeColor,
              color: "#fff",
              fontSize: "0.75rem",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {totalSelected}
          </Box>

          {/* Image + toggle */}
          <Box
            component="img"
            src={imageSrc}
            alt={`${upperPrefix} framework`}
            sx={{
              width: open ? 40 : 72,
              height: open ? 40 : 72,
              borderRadius: 1,
              objectFit: "cover",
              alignSelf: "flex-start",
            }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => setOpen((o) => !o)}
            endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              textTransform: "none",
              ml: 1,
              borderRadius: 2,
              fontWeight: 600,
              alignSelf: "center",
              borderColor: "divider",
              color: "text.primary"
            }}
          >
            {open ? "Hide MLUs" : "Show MLUs"}
          </Button>
        </Box>
      </Stack>

      {/* Expanded chip list */}
      {open && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1.5 }}>
            {certificationNeeded
              ? "🔗 Certification mode: paired MLUs are linked. Clicking one selects both."
              : "Click on an MLU to select it. Click again to deselect."}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {renderChips()}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

export { FrameworkSelector, EDU_MLUS, GOV_MLUS, TEC_MLUS, EID_MLUS };
